import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { GeoSetTargetable } from '@feathr/blackbox';
import { TargetableClass } from '@feathr/blackbox';
import { Button, EmptyState, Icon, Table } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

import geoFiltersColumns from './geoFiltersColumns';

interface IGeoFiltersTableProps {
  defaultGeoSetId?: string;
  onCreateSet: () => void;
  onEditSet: (targetable: GeoSetTargetable) => void;
  onSetDefault: (targetable: GeoSetTargetable) => void;
}

function GeoFiltersTable({
  defaultGeoSetId,
  onCreateSet,
  onEditSet,
  onSetDefault,
}: Readonly<IGeoFiltersTableProps>): JSX.Element {
  const { t } = useTranslation();
  const { Targetables, Targetings } = useStore();

  const geoSetTargetables = Targetables.list({
    filters: { is_archived__ne: true, _cls: TargetableClass.geo_set },
  });

  const geoSetTargetings = Targetings.list({
    filters: { kind: 'geo_set', is_archived__ne: true },
  });

  const campaignCountsByGeoSetId = new Map<string, number>();
  for (const targeting of geoSetTargetings.models) {
    const targetData = targeting.get('target_data');
    if (targetData) {
      campaignCountsByGeoSetId.set(targetData, (campaignCountsByGeoSetId.get(targetData) || 0) + 1);
    }
  }

  if (geoSetTargetables.isLoaded && geoSetTargetables.models.length === 0) {
    return (
      <EmptyState
        description={t('Get started by creating a new set.')}
        height={'full'}
        label={t('No geofilters')}
        theme={'white'}
        width={'full'}
      >
        <Button onClick={onCreateSet} prefix={<Icon icon={faPlus} />}>
          {t('Create set')}
        </Button>
      </EmptyState>
    );
  }

  const models = geoSetTargetables.models as unknown as GeoSetTargetable[];
  const sortedModels = [...models].sort((a, b) => {
    const aIsDefault = a.isDefault(defaultGeoSetId);
    const bIsDefault = b.isDefault(defaultGeoSetId);
    if (aIsDefault && !bIsDefault) {
      return -1;
    }
    if (!aIsDefault && bIsDefault) {
      return 1;
    }
    return (a.get('name') || '').localeCompare(b.get('name') || '');
  });

  return (
    <Table<GeoSetTargetable>
      columns={geoFiltersColumns(
        t,
        onEditSet,
        onSetDefault,
        defaultGeoSetId,
        campaignCountsByGeoSetId,
      )}
      idKey={'id'}
      initialPagesize={10}
      isLoading={geoSetTargetables.isPending}
      items={sortedModels}
    />
  );
}

export default observer(GeoFiltersTable);
